import { useEffect, useState } from "react";
import { useGetVendorsDropdown } from "hooks/useVendors";
import { useGetProductsDropdown } from "hooks/useProducts";
import { adminUpdateProductVideo, vendorUpdateProductVideo } from "services/productVideos";

/**
 * Edit modal — re-assign the video to a different product, optionally
 * change the caption. Same shape as the upload modal but without the
 * file pickers (the underlying video file doesn't change).
 *
 * Scope:
 *  - admin: can pick any vendor + any of that vendor's products
 *  - vendor: vendor is locked to self; products dropdown is scoped to own
 *
 * On save, status is preserved (moderation was about content, not slot).
 */
const ProductVideoEditModal = ({ scope, video, currentUser, onClose, onSaved }) => {
    const isAdminScope = scope === "admin";
    const { vendors } = useGetVendorsDropdown();

    // Initial vendor:
    //  - admin: from the video being edited
    //  - vendor: always the logged-in vendor
    const initialVendorId = isAdminScope
        ? (video?.vendorId ?? video?.vendor?.id ?? "")
        : (currentUser?.id ?? "");
    const [vendorId, setVendorId] = useState(initialVendorId);
    const [productId, setProductId] = useState(video?.productId ?? video?.product?.id ?? "");
    const [caption, setCaption] = useState(video?.caption ?? "");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const { products, loading: productsLoading } = useGetProductsDropdown(
        { vendorId },
        { enabled: Boolean(vendorId) },
    );

    // When admin switches the vendor, clear the product selection so they
    // don't accidentally save a stale product id from the previous vendor.
    useEffect(() => {
        if (!isAdminScope) return;
        if (Number(vendorId) !== Number(initialVendorId)) {
            setProductId("");
        }
    }, [vendorId, initialVendorId, isAdminScope]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (!productId) return setError("اختر المنتج");

        const body = {
            productId: Number(productId),
            caption: caption || null,
        };

        setSubmitting(true);
        try {
            const update = isAdminScope ? adminUpdateProductVideo : vendorUpdateProductVideo;
            await update(video.id, body);
            onSaved?.();
            onClose();
        } catch (err) {
            setError(err.message || "فشل التحديث");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="product-video-modal-backdrop" onClick={onClose}>
            <div className="product-video-modal" onClick={(e) => e.stopPropagation()}>
                <header>
                    <h2>تعديل الفيديو</h2>
                    <button type="button" className="close" onClick={onClose}>
                        ×
                    </button>
                </header>
                <form onSubmit={handleSubmit}>
                    {isAdminScope ? (
                        <label>
                            <span>المطعم</span>
                            <select
                                value={vendorId}
                                onChange={(e) => setVendorId(e.target.value)}
                                required
                            >
                                <option value="">— اختر المطعم —</option>
                                {vendors.map((v) => (
                                    <option key={v.id} value={v.id}>
                                        {v.name}
                                    </option>
                                ))}
                            </select>
                        </label>
                    ) : null}

                    <label>
                        <span>المنتج</span>
                        <select
                            value={productId}
                            onChange={(e) => setProductId(e.target.value)}
                            required
                            disabled={!vendorId || productsLoading}
                        >
                            <option value="">
                                {productsLoading
                                    ? "...جار التحميل"
                                    : !vendorId
                                      ? "اختر المطعم أولًا"
                                      : products.length === 0
                                        ? "لا توجد منتجات"
                                        : "— اختر المنتج —"}
                            </option>
                            {products.map((p) => (
                                <option key={p.id} value={p.id}>
                                    {p.title}
                                </option>
                            ))}
                        </select>
                    </label>

                    <label>
                        <span>نص قصير (اختياري)</span>
                        <input
                            type="text"
                            value={caption}
                            onChange={(e) => setCaption(e.target.value)}
                            maxLength={500}
                            placeholder="مثال: حار وطازج ومش غالي"
                        />
                    </label>

                    {error ? <p className="error">{error}</p> : null}

                    <div className="actions">
                        <button type="button" onClick={onClose}>
                            إلغاء
                        </button>
                        <button type="submit" className="primary" disabled={submitting}>
                            {submitting ? "...جارٍ الحفظ" : "حفظ التعديلات"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ProductVideoEditModal;
